import {
  ArbitrationDTOStatusEnum,
  ClaimProposalDTO,
  ClaimProposalDTOSourceEnum,
  ClaimProposalDTOStatusEnum,
  ClaimWithCheckoutRelationsDTO,
  ClaimWithCheckoutRelationsDTOStatusEnum,
  OrderWithOrderCustomersDTO,
  OrderWithOrderCustomersDTOStatusIdEnum,
} from '@reposit/api-client';
import { isPaymentPlanInterestCollectible } from '../../../utils/payment-plan.utils';

export enum ClaimFinancialSummaryStatus {
  NOT_READY = 'NOT_READY',
  READY = 'READY',
  INCONSISTENT = 'INCONSISTENT',
}

export enum ClaimFinancialSummaryNotReadyReason {
  CLAIM_NOT_LOADED = 'CLAIM_NOT_LOADED',
  NEGOTIATION_IN_PROGRESS = 'NEGOTIATION_IN_PROGRESS',
  ARBITRATION_IN_PROGRESS = 'ARBITRATION_IN_PROGRESS',
}

export enum ClaimFinancialSummaryInconsistencyReason {
  FINAL_AMOUNT_WITHOUT_PROPOSAL = 'FINAL_AMOUNT_WITHOUT_PROPOSAL',
  FINAL_AMOUNT_WITH_NON_FINAL_PROPOSAL = 'FINAL_AMOUNT_WITH_NON_FINAL_PROPOSAL',
  CURRENT_PROPOSAL_COUNTERED = 'CURRENT_PROPOSAL_COUNTERED',
  AGREED_PROPOSAL_FINAL_AMOUNT_MISSING = 'AGREED_PROPOSAL_FINAL_AMOUNT_MISSING',
  AGREED_PROPOSAL_ORDER_MISSING = 'AGREED_PROPOSAL_ORDER_MISSING',
  PUBLISHED_ARBITRATION_AWARD_MISSING = 'PUBLISHED_ARBITRATION_AWARD_MISSING',
  PUBLISHED_ARBITRATION_CLAIM_AMOUNT_MISSING = 'PUBLISHED_ARBITRATION_CLAIM_AMOUNT_MISSING',
  PUBLISHED_ARBITRATION_FEE_CREDIT_INVALID = 'PUBLISHED_ARBITRATION_FEE_CREDIT_INVALID',
  PUBLISHED_ARBITRATION_ORDER_MISSING = 'PUBLISHED_ARBITRATION_ORDER_MISSING',
  PUBLISHED_ARBITRATION_ORDER_UNEXPECTED = 'PUBLISHED_ARBITRATION_ORDER_UNEXPECTED',
  ARBITRATED_CLAIM_PUBLISHED_ARBITRATION_MISSING = 'ARBITRATED_CLAIM_PUBLISHED_ARBITRATION_MISSING',
  ORDER_BALANCE_EXCEEDS_ORDER_AMOUNT = 'ORDER_BALANCE_EXCEEDS_ORDER_AMOUNT',
  INVALID_AMOUNT = 'INVALID_AMOUNT',
}

interface ClaimFinancialSummaryNotReadyState {
  status: ClaimFinancialSummaryStatus.NOT_READY;
  reason: ClaimFinancialSummaryNotReadyReason;
  explanation: string;
}

interface ClaimFinancialSummaryOrderCollection {
  kind: 'ORDER';
  order: OrderWithOrderCustomersDTO;
}

interface ClaimFinancialSummaryKnownCollection {
  kind: 'KNOWN';
  totalPaid: number;
}

export interface ClaimFinancialSummaryReadyState {
  status: ClaimFinancialSummaryStatus.READY;
  claimId: string;
  basis: 'PROPOSAL' | 'ARBITRATION';
  finalAmount: number;
  finalAmountLabel: string;
  explanation: string;
  collection: ClaimFinancialSummaryOrderCollection | ClaimFinancialSummaryKnownCollection;
}

export interface ClaimFinancialSummaryInconsistentState {
  status: ClaimFinancialSummaryStatus.INCONSISTENT;
  reason: ClaimFinancialSummaryInconsistencyReason;
  claimId: string;
  explanation: string;
}

export type ClaimFinancialSummaryState =
  | ClaimFinancialSummaryNotReadyState
  | ClaimFinancialSummaryReadyState
  | ClaimFinancialSummaryInconsistentState;

export interface ClaimFinancialSummaryReportContext {
  claimId: string;
  reason: ClaimFinancialSummaryInconsistencyReason;
}

export type ClaimFinancialSummaryReporter = (error: Error, context: ClaimFinancialSummaryReportContext) => void;

const NOT_READY_EXPLANATION =
  'The payments summary will be available once a claim proposal has been agreed or an arbitration award has been published.';
const ARBITRATION_IN_PROGRESS_EXPLANATION =
  'The payments summary will be available once the arbitration award has been published.';

const getPublishedArbitration = (claim: ClaimWithCheckoutRelationsDTO) => {
  const arbitration = claim.arbitration;
  return arbitration && arbitration.status === ArbitrationDTOStatusEnum.PUBLISHED ? arbitration : undefined;
};

const getCurrentProposal = (claim: ClaimWithCheckoutRelationsDTO): ClaimProposalDTO | undefined => {
  return (claim.proposals || []).reduce<ClaimProposalDTO | undefined>((latestProposal, proposal) => {
    return !latestProposal || proposal.round > latestProposal.round ? proposal : latestProposal;
  }, undefined);
};

const getLiabilityCapExplanation = (amount: number, limit: number) => {
  return amount > limit ? " It is capped at the claim's maximum liability." : '';
};

const getProposalExplanation = (claim: ClaimWithCheckoutRelationsDTO) => {
  return `The order amount is the agreed claim amount.${getLiabilityCapExplanation(
    claim.finalAmount as number,
    claim.limit,
  )} Total paid is the amount collected against it and excludes payment-plan interest.`;
};

const getArbitrationExplanation = (claim: ClaimWithCheckoutRelationsDTO, arbitratedAmount: number) => {
  return `The order amount is the published arbitration award.${getLiabilityCapExplanation(
    arbitratedAmount,
    claim.limit,
  )} Total paid is the amount collected against it, including any adjudication-fee credit, and excludes payment-plan interest.`;
};

const notReady = (
  reason: ClaimFinancialSummaryNotReadyReason,
  explanation: string = NOT_READY_EXPLANATION,
): ClaimFinancialSummaryNotReadyState => ({
  status: ClaimFinancialSummaryStatus.NOT_READY,
  reason,
  explanation,
});

const inconsistent = (
  claim: ClaimWithCheckoutRelationsDTO,
  reason: ClaimFinancialSummaryInconsistencyReason,
  explanation: string,
): ClaimFinancialSummaryInconsistentState => ({
  status: ClaimFinancialSummaryStatus.INCONSISTENT,
  reason,
  claimId: claim.id,
  explanation,
});

const getPublishedArbitrationAmountDue = (claim: ClaimWithCheckoutRelationsDTO, arbitratedAmount: number): number | undefined => {
  const canonicalAmount = Math.min(arbitratedAmount, claim.limit);
  if (claim.disputeFeeWaived) return canonicalAmount;
  if (typeof claim.finalAmount !== 'number') return undefined;

  const awardWasReduced = arbitratedAmount < claim.finalAmount;
  const awardIsBelowLiabilityLimit = arbitratedAmount < claim.limit;
  if (!awardWasReduced || !awardIsBelowLiabilityLimit) return canonicalAmount;

  const adjudicationFeeOrder = claim.adjudicationFeeOrder;
  if (!adjudicationFeeOrder || adjudicationFeeOrder.statusId !== OrderWithOrderCustomersDTOStatusIdEnum.COMPLETE) {
    return undefined;
  }

  return arbitratedAmount - adjudicationFeeOrder.amount;
};

const getPublishedArbitrationState = (
  claim: ClaimWithCheckoutRelationsDTO,
): ClaimFinancialSummaryReadyState | ClaimFinancialSummaryInconsistentState | undefined => {
  const arbitration = getPublishedArbitration(claim);
  if (!arbitration) return undefined;

  if (typeof arbitration.arbitratedAmount !== 'number') {
    return inconsistent(
      claim,
      ClaimFinancialSummaryInconsistencyReason.PUBLISHED_ARBITRATION_AWARD_MISSING,
      'The payments summary is unavailable because the published arbitration award is missing.',
    );
  }
  if (typeof claim.finalAmount !== 'number') {
    return inconsistent(
      claim,
      ClaimFinancialSummaryInconsistencyReason.PUBLISHED_ARBITRATION_CLAIM_AMOUNT_MISSING,
      'The payments summary is unavailable because the claim amount used for arbitration is missing.',
    );
  }

  const finalAmount = Math.min(arbitration.arbitratedAmount, claim.limit);
  if (finalAmount < 0) {
    return inconsistent(
      claim,
      ClaimFinancialSummaryInconsistencyReason.INVALID_AMOUNT,
      'The payments summary is unavailable because the published arbitration amount is invalid.',
    );
  }

  const readyState = (collection: ClaimFinancialSummaryReadyState['collection']): ClaimFinancialSummaryReadyState => ({
    status: ClaimFinancialSummaryStatus.READY,
    claimId: claim.id,
    basis: 'ARBITRATION',
    finalAmount,
    finalAmountLabel: arbitration.arbitratedAmount > claim.limit ? 'Arbitration (max liability)' : 'Arbitration',
    explanation: getArbitrationExplanation(claim, arbitration.arbitratedAmount),
    collection,
  });

  const amountDue = getPublishedArbitrationAmountDue(claim, arbitration.arbitratedAmount);
  if (typeof amountDue !== 'number' || !Number.isFinite(amountDue)) {
    return inconsistent(
      claim,
      ClaimFinancialSummaryInconsistencyReason.PUBLISHED_ARBITRATION_FEE_CREDIT_INVALID,
      'The payments summary is unavailable because the arbitration adjudication-fee credit cannot be validated.',
    );
  }
  if (amountDue > 0 && !arbitration.repaymentOrder) {
    return inconsistent(
      claim,
      ClaimFinancialSummaryInconsistencyReason.PUBLISHED_ARBITRATION_ORDER_MISSING,
      'The payments summary is unavailable because the published arbitration repayment order is missing.',
    );
  }
  if (amountDue > 0) {
    return readyState({ kind: 'ORDER', order: arbitration.repaymentOrder as OrderWithOrderCustomersDTO });
  }
  if (arbitration.repaymentOrder || arbitration.repaymentOrderId) {
    return inconsistent(
      claim,
      ClaimFinancialSummaryInconsistencyReason.PUBLISHED_ARBITRATION_ORDER_UNEXPECTED,
      'The payments summary is unavailable because this fully covered arbitration has an unexpected repayment order.',
    );
  }

  return readyState({ kind: 'KNOWN', totalPaid: finalAmount });
};

export const getClaimFinancialSummaryState = (claim?: ClaimWithCheckoutRelationsDTO): ClaimFinancialSummaryState => {
  if (!claim) return notReady(ClaimFinancialSummaryNotReadyReason.CLAIM_NOT_LOADED);

  const publishedArbitrationState = getPublishedArbitrationState(claim);
  if (publishedArbitrationState) return publishedArbitrationState;

  if (claim.status === ClaimWithCheckoutRelationsDTOStatusEnum.ARBITRATED) {
    return inconsistent(
      claim,
      ClaimFinancialSummaryInconsistencyReason.ARBITRATED_CLAIM_PUBLISHED_ARBITRATION_MISSING,
      'The payments summary is unavailable because this arbitrated claim has no published arbitration award.',
    );
  }

  if (claim.arbitration && claim.arbitration.status === ArbitrationDTOStatusEnum.DRAFT) {
    return notReady(ClaimFinancialSummaryNotReadyReason.ARBITRATION_IN_PROGRESS, ARBITRATION_IN_PROGRESS_EXPLANATION);
  }

  const currentProposal = getCurrentProposal(claim);
  if (!currentProposal) {
    return typeof claim.finalAmount === 'number'
      ? inconsistent(
          claim,
          ClaimFinancialSummaryInconsistencyReason.FINAL_AMOUNT_WITHOUT_PROPOSAL,
          'The payments summary is unavailable because the claim has an order amount but no proposal.',
        )
      : notReady(ClaimFinancialSummaryNotReadyReason.NEGOTIATION_IN_PROGRESS);
  }

  if (currentProposal.status === ClaimProposalDTOStatusEnum.DISPUTED) {
    return notReady(ClaimFinancialSummaryNotReadyReason.ARBITRATION_IN_PROGRESS, ARBITRATION_IN_PROGRESS_EXPLANATION);
  }

  const isAgreedProposal =
    currentProposal.status === ClaimProposalDTOStatusEnum.SETTLED ||
    (currentProposal.status === ClaimProposalDTOStatusEnum.AWAITINGPAYMENT &&
      currentProposal.source === ClaimProposalDTOSourceEnum.SUPPLIER);

  if (!isAgreedProposal) {
    if (currentProposal.status === ClaimProposalDTOStatusEnum.COUNTERED) {
      return inconsistent(
        claim,
        ClaimFinancialSummaryInconsistencyReason.CURRENT_PROPOSAL_COUNTERED,
        'The payments summary is unavailable because the countered proposal has no newer proposal.',
      );
    }
    if (typeof claim.finalAmount === 'number') {
      return inconsistent(
        claim,
        ClaimFinancialSummaryInconsistencyReason.FINAL_AMOUNT_WITH_NON_FINAL_PROPOSAL,
        'The payments summary is unavailable because the order amount is still set while negotiation is in progress.',
      );
    }
    return notReady(ClaimFinancialSummaryNotReadyReason.NEGOTIATION_IN_PROGRESS);
  }

  if (typeof claim.finalAmount !== 'number') {
    return inconsistent(
      claim,
      ClaimFinancialSummaryInconsistencyReason.AGREED_PROPOSAL_FINAL_AMOUNT_MISSING,
      'The payments summary is unavailable because the agreed proposal has no order amount.',
    );
  }

  const finalAmount = Math.min(claim.finalAmount, claim.limit);
  if (finalAmount < 0) {
    return inconsistent(
      claim,
      ClaimFinancialSummaryInconsistencyReason.INVALID_AMOUNT,
      'The payments summary is unavailable because the agreed order amount is invalid.',
    );
  }
  const mayOmitOrder =
    finalAmount === 0 &&
    currentProposal.source === ClaimProposalDTOSourceEnum.TENANT &&
    currentProposal.status === ClaimProposalDTOStatusEnum.SETTLED &&
    !currentProposal.orderId;
  if (!currentProposal.order && !mayOmitOrder) {
    return inconsistent(
      claim,
      ClaimFinancialSummaryInconsistencyReason.AGREED_PROPOSAL_ORDER_MISSING,
      'The payments summary is unavailable because the agreed proposal order is missing.',
    );
  }

  return {
    status: ClaimFinancialSummaryStatus.READY,
    claimId: claim.id,
    basis: 'PROPOSAL',
    finalAmount,
    finalAmountLabel: claim.finalAmount > claim.limit ? 'Claim amount (max liability)' : 'Claim amount',
    explanation: getProposalExplanation(claim),
    collection:
      finalAmount === 0
        ? { kind: 'KNOWN', totalPaid: 0 }
        : { kind: 'ORDER', order: currentProposal.order as OrderWithOrderCustomersDTO },
  };
};

export const getClaimFinancialSummaryStateWithBalance = (
  summaryState: ClaimFinancialSummaryState,
  amountRemaining?: number,
): ClaimFinancialSummaryState => {
  if (
    summaryState.status !== ClaimFinancialSummaryStatus.READY ||
    summaryState.collection.kind !== 'ORDER' ||
    typeof amountRemaining !== 'number' ||
    amountRemaining <= summaryState.collection.order.amount
  ) {
    return summaryState;
  }

  return {
    status: ClaimFinancialSummaryStatus.INCONSISTENT,
    reason: ClaimFinancialSummaryInconsistencyReason.ORDER_BALANCE_EXCEEDS_ORDER_AMOUNT,
    claimId: summaryState.claimId,
    explanation: 'The payments summary is unavailable because the order balance is greater than the order amount.',
  };
};

export const isAmountOwedUnfinalised = (summaryState: ClaimFinancialSummaryState): boolean => {
  return (
    summaryState.status === ClaimFinancialSummaryStatus.NOT_READY &&
    (summaryState.reason === ClaimFinancialSummaryNotReadyReason.NEGOTIATION_IN_PROGRESS ||
      summaryState.reason === ClaimFinancialSummaryNotReadyReason.ARBITRATION_IN_PROGRESS)
  );
};

export const getCanonicalClaimPaymentOrder = (
  summaryState: ClaimFinancialSummaryState,
): OrderWithOrderCustomersDTO | undefined => {
  return summaryState.status === ClaimFinancialSummaryStatus.READY && summaryState.collection.kind === 'ORDER'
    ? summaryState.collection.order
    : undefined;
};

export const getCanonicalClaimPaymentPlanInterest = (summaryState: ClaimFinancialSummaryState): number | undefined => {
  const paymentOrder = getCanonicalClaimPaymentOrder(summaryState);
  if (!paymentOrder) return undefined;

  const paymentPlans = (paymentOrder.orderCustomers || [])
    .map((orderCustomer) => orderCustomer.paymentPlan)
    .filter(isPaymentPlanInterestCollectible);
  if (!paymentPlans.length) return undefined;

  return paymentPlans.reduce((total, paymentPlan) => total + (paymentPlan ? paymentPlan.interestAmount || 0 : 0), 0);
};

export const getCanonicalClaimOriginalPaymentPlanInterest = (summaryState: ClaimFinancialSummaryState): number | undefined => {
  const paymentOrder = getCanonicalClaimPaymentOrder(summaryState);
  if (!paymentOrder) return undefined;

  const paymentPlans = (paymentOrder.orderCustomers || [])
    .map((orderCustomer) => orderCustomer.paymentPlan)
    .filter((paymentPlan) => !!paymentPlan);
  if (!paymentPlans.length) return undefined;

  return paymentPlans.reduce((total, paymentPlan) => total + (paymentPlan ? paymentPlan.interestAmount || 0 : 0), 0);
};

export const getPrincipalAmountRemainingFromBalance = (
  amountRemaining?: number,
  includedInterest?: number,
): number | undefined => {
  if (typeof amountRemaining !== 'number') return undefined;

  return amountRemaining - (typeof includedInterest === 'number' ? includedInterest : 0);
};

export const getTotalPaidAgainstFinalAmount = (
  summaryState: ClaimFinancialSummaryState,
  amountRemaining?: number,
): number | undefined => {
  if (summaryState.status !== ClaimFinancialSummaryStatus.READY) return undefined;
  if (summaryState.collection.kind === 'KNOWN') return summaryState.collection.totalPaid;
  if (typeof amountRemaining !== 'number') return undefined;

  return Math.min(summaryState.finalAmount, Math.max(0, summaryState.finalAmount - amountRemaining));
};

const consoleReporter: ClaimFinancialSummaryReporter = (error, context) => {
  console.error(error, context);
};

export const reportClaimFinancialSummaryInconsistency = (
  summaryState: ClaimFinancialSummaryState,
  reporter: ClaimFinancialSummaryReporter = consoleReporter,
) => {
  if (summaryState.status !== ClaimFinancialSummaryStatus.INCONSISTENT) return;

  reporter(new Error(`Inconsistent claim payments summary: ${summaryState.reason}`), {
    claimId: summaryState.claimId,
    reason: summaryState.reason,
  });
};
